import PropTypes from 'prop-types';
import { PinInput } from '../PinInput';
import classes from './PhraseInput.module.scss';
import { useEffect, useRef } from 'react';
import { createLetter } from '../../utils/functions';
import { useImperativeHandle } from 'react';
import { SIZE_INPUT } from '../../utils/constants';
import clsx from 'clsx';
import { useSelector } from 'react-redux';

export function PhraseInput(props) {
    const { words, onChange, variant = "default", size, disabled = false, variantValidation, ref } = props;
    const wordElements = useRef({});
    const currentQuestion = useSelector(state => state.game.questions[state.game.currentIndex]);

    const onWordChange = (newWord, index) => {
        const newWords = [...words];
        newWords[index] = newWord;
        onChange(newWords);
    }

    useImperativeHandle(ref, () => ({
        focusWord: (index) => {
            wordElements.current[index].focusStart();
        },
        focusFirstLetterEmpty: () => {
            const firstWordWithEmpty = words.findIndex(word => word.some(letter => !letter.value));
            const firstletterEmpty = words[firstWordWithEmpty]?.findIndex(letter => !letter.value);
            wordElements.current[firstWordWithEmpty]?.focusIndex(firstletterEmpty);
        }
    }), [words]);

    useEffect(() => {
        setTimeout(() => {
            wordElements.current[0].focusStart()
        }, currentQuestion.id === 0 ? 2900 : 350)
    }, [currentQuestion.id])

    const onNavigateNext = (index) => {
        if (index < words.length - 1) {
            wordElements.current[index + 1].focusStart();
        }
    }

    const onNavigatePrevious = (index) => {
        if (index > 0) {
            wordElements.current[index - 1].focusEnd();
        }
    }

    const onNavigateDeletePrevious = (index) => {
        if (index > 0) {
            wordElements.current[index - 1].deleteLastLetter();
        }
    }

    const handlePaste = ({ event, wordId, letterId }) => {
        event.preventDefault();
        const clipboardData = event.clipboardData.getData('Text');
        pasteContent({ content: clipboardData, wordId, letterId });
    }

    const pasteContent = ({ content, wordId, letterId }) => {
        const formattedData = content.split('').filter((char) => !/\s/g.test(char));
        const newWords = [...words];

        let wordPosition = wordId;
        let letterPosition = letterId;

        for (wordPosition; wordPosition < newWords.length; wordPosition++) {
            for (letterPosition; letterPosition < newWords[wordPosition].length; letterPosition++) {
                if (formattedData.length === 0) break;

                if (newWords[wordPosition][letterPosition].disabled) continue;

                newWords[wordPosition][letterPosition] = createLetter(formattedData.shift().toUpperCase());
            }
            if (formattedData.length === 0) break;
            if (wordPosition < newWords.length - 1) {
                letterPosition = 0;
            }
        }

        // Set the focus on the next available letter
        wordPosition = Math.min(wordPosition, newWords.length - 1);
        if (letterPosition >= newWords[wordPosition].length && wordPosition < newWords.length - 1) {
            wordPosition++;
            letterPosition = 0;
        }
        letterPosition = Math.min(letterPosition, newWords[wordPosition].length - 1);
        onChange(newWords);
        wordElements.current[wordPosition].focusIndex(letterPosition);
    }

    return (
        <div className={clsx(classes.inputWrapper, size === SIZE_INPUT.SMALL && classes.small)}>
            {words.map((word, index) => {
                return (
                    <PinInput
                        key={index}
                        letters={word}
                        disabled={disabled}
                        onChange={(newWord) => onWordChange(newWord, index)}
                        ref={(element) => wordElements.current[index] = element}
                        onNavigateNext={() => onNavigateNext(index)}
                        onNavigatePrevious={() => onNavigatePrevious(index)}
                        onNavigateDeletePrevious={() => onNavigateDeletePrevious(index)}
                        onPaste={(event, letterId) => handlePaste({ event, wordId: index, letterId })}
                        variantValidation={variantValidation}
                        variant={variant}
                        size={size}
                    />
                )
            })}
        </div>
    )
}


PhraseInput.propTypes = {
    words: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
    onChange: PropTypes.func.isRequired,
    variant: PropTypes.oneOf(['transparent', 'default']),
    variantValidation: PropTypes.oneOf(['correct', 'incorrect']),
    size: PropTypes.oneOf(['default', 'small'])
}